import {
  Badge, Text, Stack, useTranslation, formatDate,
} from '@piwikpro/ui-components';
import moment from 'moment/moment';
import React from 'react';
import { LicenseAttributes } from '@piwikpro/organization-crate/redux/details';

const LicenseDateBadge = ({
  license,
  upcomingLicense,
  mode,
}: {
  mode: 'support-panel' | 'administration'
  license: LicenseAttributes | null
  upcomingLicense?: LicenseAttributes | null
}) => {
  const { t } = useTranslation();

  if (license === null) {
    throw new Error('License not found');
  }

  const isTrial = license.plan.id === 'trial';
  const expired = license.status === 'expired';
  const inactiveToday = moment().utc().isSame(moment(license.inactive_at).utc(), 'day');
  const isInactive = license.status === 'inactive';
  const inactiveAt = moment(license.inactive_at).utc();

  const color = (() => {
    if (isInactive) { return 'gray'; }
    if (expired) { return 'gray'; }

    return 'white';
  })();

  let expiresAtFormatted = 'Forever';
  if (license.expires_at) {
    const dateLabel = formatDate(new Date(license.expires_at), { format: 'LL', timeZone: 'UTC' });
    expiresAtFormatted = mode === 'support-panel' ? dateLabel : `${dateLabel} (UTC)`;
  }

  const text = (() => {
    if (isInactive && mode === 'support-panel') { return `Expired on ${expiresAtFormatted}`; }
    if (!expired) { return `${t('platform.license.plan.active-until')} ${expiresAtFormatted}`; }
    if (mode === 'administration') { return t('platform.license.details.expirationDate.inactive'); }
    if (mode === 'support-panel') { return `Expired on ${expiresAtFormatted}`; }
    throw new Error('Cannot determine badge text');
  })();

  const expirationNote = (() => {
    if (mode === 'administration') { return null; }
    if (mode === 'support-panel' && isTrial) { return null; }
    if (upcomingLicense) { return null; }
    if (isInactive) { return 'Grace period ended. Account suspended.'; }
    if (inactiveToday) { return 'Last grace day'; }
    if (expired) { return `${moment(inactiveAt).diff(moment(), 'days')} grace days left`; }

    return null;
  })();

  return (
    <Stack spacing="narrow" verticalAlignment="center">
      <Stack.Item><Badge size="medium" color={color} text={text} /></Stack.Item>
      {expirationNote && <Stack.Item><Text as="p" size={12} color="Red" dataId="Caption">{expirationNote}</Text></Stack.Item>}
    </Stack>
  );
};

export { LicenseDateBadge };
